import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errorHandler";

interface DataEraseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DataEraseDialog({ open, onOpenChange }: DataEraseDialogProps) {
  const [pin, setPin] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  const handleErase = async () => {
    if (confirmText !== "ERASE ALL DATA") {
      toast.error("Please type 'ERASE ALL DATA' to confirm");
      return;
    }

    if (!pin) {
      toast.error("Please enter your PIN");
      return;
    }

    setLoading(true);

    try {
      // Verify PIN
      const { data: isVerified, error: verifyError } = await supabase.rpc('verify_admin_action', {
        _pin: pin,
        _action: 'Erase All Data',
        _details: { warning: 'Complete data wipe except admin accounts' }
      });

      if (verifyError || !isVerified) {
        toast.error("Invalid PIN");
        setLoading(false);
        return;
      }

      // Call edge function to erase data
      const { data, error } = await supabase.functions.invoke('erase-all-data', {
        body: {}
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to erase data');
      }

      toast.success("All data erased successfully. Admin accounts preserved.");
      setPin("");
      setConfirmText("");
      onOpenChange(false);
      
      // Reload page after short delay
      setTimeout(() => window.location.reload(), 2000);
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Erase All Data
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-sm font-semibold text-destructive mb-2">⚠️ WARNING: This action cannot be undone!</p>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>• All students will be deleted</li>
              <li>• All instructors (except admins) will be deleted</li>
              <li>• All managers will be deleted</li>
              <li>• All attendance records will be erased</li>
              <li>• All flight slots will be removed</li>
              <li>• All user accounts (except admins) will be deleted</li>
              <li>• Admin accounts will be preserved</li>
            </ul>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmText">Type "ERASE ALL DATA" to confirm</Label>
            <Input
              id="confirmText"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ERASE ALL DATA"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pin">Enter Admin PIN (2030)</Label>
            <Input
              id="pin"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter PIN"
              maxLength={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setPin("");
              setConfirmText("");
              onOpenChange(false);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleErase}
            disabled={loading || confirmText !== "ERASE ALL DATA"}
          >
            {loading ? "Erasing..." : "Erase All Data"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}